import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
  Divider,
  Button,
  Paper,
} from '@mui/material';
import {
  ExpandMore,
  Search,
  Help,
  Inventory,
  LocalShipping,
  TrendingUp,
  Handshake,
  People,
  Settings,
  Dashboard,
  SmartToy,
  Storage,
  Security,
  Speed,
  CheckCircle,
  PlayArrow,
  Info,
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface FAQItem {
  question: string;
  answer: string;
  category: string;
  tags: string[];
}

const faqItems: FAQItem[] = [
  // Getting Started
  {
    category: 'Getting Started',
    question: 'What is ASCIN?',
    answer: 'ASCIN (Adaptive Supply Chain Intelligence Network) is an AI-powered supply chain management platform that uses intelligent agents to optimize your operations. It provides real-time monitoring, predictive analytics, automated supplier negotiations, and risk management capabilities.',
    tags: ['overview', 'introduction'],
  },
  {
    category: 'Getting Started',
    question: 'How do I update my company information after onboarding?',
    answer: 'Click on "Company Profile" in the sidebar (or search for "onboarding" in the app bar search box). The Company Profile page allows you to edit all the information you entered during onboarding, including company details, products, suppliers, warehouses, and preferences. You can add new suppliers, products, or locations at any time as your business grows.',
    tags: ['onboarding', 'company', 'profile', 'setup'],
  },
  {
    category: 'Getting Started',
    question: 'Will I see the onboarding wizard every time I log in?',
    answer: 'No. The onboarding wizard only appears once for new users. After you complete onboarding, you\'ll go directly to the Dashboard on future logins. To update your business information later, use the "Company Profile" page accessible from the sidebar.',
    tags: ['onboarding', 'login', 'setup'],
  },
  {
    category: 'Getting Started',
    question: 'How do I set up my first supplier?',
    answer: 'Navigate to Supply Chain → Suppliers, click "Add Supplier", fill in the required information (name, contact, location), and save. You can then associate products and track performance metrics for each supplier.',
    tags: ['suppliers', 'setup'],
  },
  {
    category: 'Getting Started',
    question: 'How do I add products to inventory?',
    answer: 'Go to Supply Chain → Products and click "Add Product". Fill in the 6-tab form with product details including basic info, inventory settings, logistics, compliance, supplier data, and sustainability metrics. The system will automatically track stock levels and trigger reorder alerts.',
    tags: ['products', 'inventory'],
  },

  // Negotiations
  {
    category: 'Negotiations',
    question: 'How does AI-powered negotiation work?',
    answer: 'The negotiation system uses game theory and AI to optimize supplier negotiations. You create a negotiation session, set your target prices and terms, and the AI agent analyzes market conditions, supplier history, and your BATNA (Best Alternative to Negotiated Agreement) to suggest optimal strategies. The system can execute multi-round negotiations automatically or provide guidance for manual negotiations.',
    tags: ['ai', 'suppliers', 'automation'],
  },
  {
    category: 'Negotiations',
    question: 'What is BATNA and why is it important?',
    answer: 'BATNA (Best Alternative to Negotiated Agreement) is your fallback option if negotiations fail. In ASCIN, you define your BATNA when creating a negotiation session - this could be an alternative supplier, different product, or making in-house. The AI uses your BATNA to determine the minimum acceptable terms and optimal negotiation pressure points.',
    tags: ['strategy', 'suppliers'],
  },
  {
    category: 'Negotiations',
    question: 'How do I start a supplier negotiation?',
    answer: 'Go to Negotiations page, click "New Negotiation", select the supplier, choose products to negotiate, set your target price/terms, define your BATNA, and click "Start Negotiation". The AI will analyze the situation and either auto-negotiate or provide strategy recommendations based on your settings.',
    tags: ['how-to', 'suppliers'],
  },
  {
    category: 'Negotiations',
    question: 'Can I negotiate multiple items at once?',
    answer: 'Yes! When creating a negotiation session, you can add multiple products or line items. The AI considers the total deal value and may suggest bundling strategies to get better overall terms while giving concessions on less critical items.',
    tags: ['strategy', 'products'],
  },

  // AI Agents
  {
    category: 'AI Agents',
    question: 'What AI agents are available?',
    answer: 'ASCIN includes several specialized agents: Demand Forecaster (predicts demand patterns), Supply Monitor (tracks supplier performance), Risk Analyzer (identifies supply chain risks), Game Theorist (negotiation strategies), and Optimization Engine (improves efficiency). Each can be configured and trained on your specific data.',
    tags: ['ai', 'automation'],
  },
  {
    category: 'AI Agents',
    question: 'How do I configure agent behavior?',
    answer: 'Go to Agents → Agent Dashboard. Each agent has settings for autonomy level (how much it can act independently), notification preferences, and specific parameters. You can enable/disable agents and adjust their sensitivity to different scenarios.',
    tags: ['ai', 'configuration'],
  },
  {
    category: 'AI Agents',
    question: 'What does the agent memory system do?',
    answer: 'The memory system allows agents to learn from past interactions, remember supplier behavior patterns, and improve recommendations over time. You can view memory statistics on the Dashboard and analyze specific memories in the Agent section.',
    tags: ['ai', 'learning'],
  },

  // Inventory
  {
    category: 'Inventory',
    question: 'How does automatic reordering work?',
    answer: 'When stock falls below the reorder point you set for each product, the system creates a reorder recommendation. Based on your settings, it can either notify you or automatically generate a purchase order. The reorder quantity is calculated using EOQ (Economic Order Quantity) or your specified fixed quantity.',
    tags: ['automation', 'products'],
  },
  {
    category: 'Inventory',
    question: 'What is safety stock and how is it calculated?',
    answer: 'Safety stock is buffer inventory to protect against demand variability and supply delays. ASCIN calculates recommended safety stock using your demand forecast accuracy, supplier lead time variability, and desired service level. You can override with manual values if needed.',
    tags: ['products', 'planning'],
  },
  {
    category: 'Inventory',
    question: 'How do I track items across multiple warehouses?',
    answer: 'Each product can have inventory in multiple locations. Go to Supply Chain → Warehouses to set up locations, then in Products you can view and transfer inventory between locations. The system tracks stock by location and can optimize distribution.',
    tags: ['warehouses', 'products'],
  },

  // Analytics & Forecasting
  {
    category: 'Analytics',
    question: 'How accurate is the demand forecasting?',
    answer: 'Forecast accuracy depends on historical data quality and demand patterns. ASCIN uses ML models including time-series analysis and considers seasonality, trends, and external factors. The Dashboard shows your current forecast accuracy percentage - typically 80-95% with sufficient historical data.',
    tags: ['ai', 'planning'],
  },
  {
    category: 'Analytics',
    question: 'What does the Resilience Score measure?',
    answer: 'The Resilience Score (0-100) measures your supply chain\'s ability to withstand disruptions. It considers: supplier diversity, inventory buffers, lead time variability, demand forecast accuracy, logistics flexibility, and supplier reliability. Higher scores indicate better risk preparedness.',
    tags: ['risk', 'metrics'],
  },
  {
    category: 'Analytics',
    question: 'How do I interpret sustainability metrics?',
    answer: 'Sustainability metrics track environmental impact: Carbon Footprint (CO2 emissions from transport, manufacturing), Renewable Energy % (green energy in operations), Waste Reduction (packaging/material efficiency), and Water Conservation. Use these to meet ESG goals and identify improvement areas.',
    tags: ['sustainability', 'metrics'],
  },

  // Settings & Security
  {
    category: 'Settings',
    question: 'How do I change notification preferences?',
    answer: 'Go to Settings → Notifications. You can enable/disable email and in-app notifications for different event types: low stock alerts, disruption warnings, negotiation updates, task completions, and system announcements. Set thresholds for what triggers each notification.',
    tags: ['notifications', 'configuration'],
  },
  {
    category: 'Settings',
    question: 'How secure is my supply chain data?',
    answer: 'ASCIN uses industry-standard security: encrypted data in transit (TLS) and at rest, role-based access control, audit logging for all actions, and secure API authentication. Enterprise deployments can integrate with SSO and comply with SOC2/GDPR requirements.',
    tags: ['security', 'compliance'],
  },
];

const featureGuides = [
  {
    title: 'Supplier Negotiations',
    icon: <Handshake />,
    description: 'Learn to leverage AI for better supplier deals',
    steps: [
      'Go to Negotiations from the sidebar menu',
      'Click "New Negotiation" to start a session',
      'Select your supplier and products to negotiate',
      'Set target prices and acceptable ranges',
      'Define your BATNA (backup plan if negotiation fails)',
      'Choose auto-negotiate or manual mode',
      'Review AI recommendations and execute rounds',
      'Accept final terms or continue negotiating',
    ],
  },
  {
    title: 'Inventory Management',
    icon: <Inventory />,
    description: 'Set up efficient stock tracking and reordering',
    steps: [
      'Add your products with complete details',
      'Set reorder points and safety stock levels',
      'Configure EOQ or fixed order quantities',
      'Link products to suppliers and warehouses',
      'Enable automatic reorder notifications',
      'Monitor stock levels on the Dashboard',
      'Process receiving when orders arrive',
      'Track movement history and costs',
    ],
  },
  {
    title: 'AI Agent Configuration',
    icon: <SmartToy />,
    description: 'Configure intelligent agents for automation',
    steps: [
      'Navigate to Agents → Agent Dashboard',
      'Review available agents and their purposes',
      'Enable agents you want to use',
      'Set autonomy levels (notify vs auto-act)',
      'Configure agent-specific parameters',
      'Monitor agent activity in task queue',
      'Review recommendations before executing',
      'Analyze agent memory and learning',
    ],
  },
  {
    title: 'Risk Monitoring',
    icon: <Security />,
    description: 'Proactively identify and mitigate supply chain risks',
    steps: [
      'Check Dashboard for active disruptions',
      'Review Resilience Score components',
      'Set up supplier diversity thresholds',
      'Configure disruption alert thresholds',
      'Enable Risk Analyzer agent',
      'Review automated risk assessments',
      'Create contingency plans for critical items',
      'Monitor external risk factors',
    ],
  },
];

const HelpPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  const filteredFaqs = faqItems.filter(
    (item) =>
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const categories = Array.from(new Set(faqItems.map((item) => item.category)));

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Getting Started': <PlayArrow />,
      'Negotiations': <Handshake />,
      'AI Agents': <SmartToy />,
      'Inventory': <Inventory />,
      'Analytics': <TrendingUp />,
      'Settings': <Settings />,
    };
    return icons[category] || <Help />;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          <Help sx={{ mr: 1, verticalAlign: 'middle' }} />
          Help Center & FAQ
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Welcome to ASCIN Help</AlertTitle>
        Find answers to common questions, step-by-step guides, and detailed feature explanations.
        Can't find what you need? Use the AI Chat for personalized assistance.
      </Alert>

      {/* Search */}
      <TextField
        fullWidth
        placeholder="Search for help topics, features, or questions..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
        <Tab label="FAQ" icon={<Help />} iconPosition="start" />
        <Tab label="Feature Guides" icon={<PlayArrow />} iconPosition="start" />
        <Tab label="Quick Reference" icon={<Speed />} iconPosition="start" />
      </Tabs>

      {/* FAQ Tab */}
      <TabPanel value={tabValue} index={0}>
        {searchQuery ? (
          // Show filtered results
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {filteredFaqs.length} result(s) for "{searchQuery}"
            </Typography>
            {filteredFaqs.map((item, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === `faq-${index}`}
                onChange={(_, isExpanded) => setExpandedFaq(isExpanded ? `faq-${index}` : false)}
              >
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {getCategoryIcon(item.category)}
                    <Box>
                      <Typography fontWeight={600}>{item.question}</Typography>
                      <Chip label={item.category} size="small" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{item.answer}</Typography>
                  <Box sx={{ mt: 2 }}>
                    {item.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                        onClick={() => setSearchQuery(tag)}
                      />
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          // Show by category
          <Grid container spacing={3}>
            {categories.map((category) => (
              <Grid item xs={12} key={category}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(category)}
                  {category}
                </Typography>
                {faqItems
                  .filter((item) => item.category === category)
                  .map((item, index) => (
                    <Accordion
                      key={index}
                      expanded={expandedFaq === `${category}-${index}`}
                      onChange={(_, isExpanded) =>
                        setExpandedFaq(isExpanded ? `${category}-${index}` : false)
                      }
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography fontWeight={500}>{item.question}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography sx={{ mb: 2 }}>{item.answer}</Typography>
                        <Box>
                          {item.tags.map((tag) => (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 0.5 }}
                              onClick={() => setSearchQuery(tag)}
                            />
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Grid>
            ))}
          </Grid>
        )}
      </TabPanel>

      {/* Feature Guides Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {featureGuides.map((guide, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {guide.icon}
                    <Typography variant="h6">{guide.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {guide.description}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Step-by-step:
                  </Typography>
                  <List dense>
                    {guide.steps.map((step, stepIndex) => (
                      <ListItem key={stepIndex}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Chip label={stepIndex + 1} size="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={step} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Quick Reference Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <Dashboard sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Key Metrics Explained
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Resilience Score" 
                      secondary="0-100 score measuring supply chain disruption preparedness"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Forecast Accuracy" 
                      secondary="Percentage of demand predictions that matched actual orders"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Carbon Footprint" 
                      secondary="Total CO2 emissions in kg across all operations"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Agent Success Rate" 
                      secondary="Percentage of AI agent tasks completed without errors"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <Handshake sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Negotiation Terms Glossary
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="BATNA" 
                      secondary="Best Alternative to Negotiated Agreement - your backup plan"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="ZOPA" 
                      secondary="Zone of Possible Agreement - overlap where deal is possible"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Reservation Price" 
                      secondary="Your absolute minimum/maximum acceptable price"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Anchor" 
                      secondary="First offer that sets expectations for negotiation"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <Storage sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Inventory Terms
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Safety Stock" 
                      secondary="Buffer inventory to prevent stockouts during variability"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Reorder Point" 
                      secondary="Stock level that triggers a new purchase order"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="EOQ" 
                      secondary="Economic Order Quantity - optimal order size to minimize costs"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><Info color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Lead Time" 
                      secondary="Days from order placement to delivery receipt"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <SmartToy sx={{ mr: 1, verticalAlign: 'middle' }} />
                  AI Agent Types
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Demand Forecaster" 
                      secondary="Predicts future product demand using ML models"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Supply Monitor" 
                      secondary="Tracks supplier performance and delivery reliability"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Risk Analyzer" 
                      secondary="Identifies potential disruptions and mitigation strategies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><SmartToy color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Game Theorist" 
                      secondary="Optimizes negotiation strategies using game theory"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mt: 3, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Need More Help?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Our AI assistant can answer specific questions about your data and provide personalized guidance.
          </Typography>
          <Button variant="contained" color="secondary" href="/chat">
            Open AI Chat
          </Button>
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default HelpPage;
